import { useCallback } from 'react'
import { useFormContext, FieldValues, FieldPath, PathValue, Path } from 'react-hook-form'

type TComponentFactory = <T>(v: string, r: T, i: number) => JSX.Element | string
type TConditionFactory = (elementsFactory: [TComponentFactory, TComponentFactory]) => TComponentFactory

export const useRow = <T extends FieldValues>(fieldName: FieldPath<T>) => {
  const { watch, getValues, control, setValue } = useFormContext<T>()

  const condition = useCallback<TConditionFactory>(
    (els) => (value, record, idx) => els[Number(watch(fieldName) === idx)](value, record, idx),
    [fieldName, watch]
  )

  const onRow = useCallback(
    (_, idx) => {
      if (getValues(fieldName) === idx) {
        return { 'data-edited': 'true', id: idx.toString() }
      }
      return { id: idx.toString() }
    },
    [fieldName, getValues]
  )

  const handleEdit = useCallback(
    (idx: PathValue<T, Path<T>>) => {
      setValue(fieldName, idx)
    },
    [fieldName, setValue]
  )

  return {
    control,
    condition,
    onRow,
    onEdit: handleEdit
  }
}
